import React, { useState } from "react";
import { Plan2PlateDrawer, Plan2PlateDrawerHeader } from "./Plan2PlateDrawer";
import { ColorModeSwitcher } from "../ColorModeSwitcher";
import { Switch } from "./ui/Toggle";
import {
  TrackingMode,
  TRACKING_MODE_DESCRIPTIONS,
  TRACKING_MODE_LABELS,
} from "../data/settings";
import { Plan2PlateIcon } from "./Plan2PlateIcon";
import { Button } from "./ui/Button";
import { keycloak } from "../Keycloak";
import { GroupSection } from "./settings/GroupSection";

const MODES: TrackingMode[] = ["restriction", "flex", "balance", "none"];

export const SettingsDrawer = (props: {
  open: boolean;
  onClose: () => void;
  trackingMode: TrackingMode;
  onTrackingModeChange: (mode: TrackingMode) => void;
  showHints: boolean;
  onShowHintsChange: (show: boolean) => void;
}) => {
  const [showUnitHelper, setShowUnitHelper] = useState(() => {
    const saved = localStorage.getItem("showUnitHelper");
    return saved === "true";
  });

  const handleToggleUnitHelper = (checked: boolean) => {
    setShowUnitHelper(checked);
    localStorage.setItem("showUnitHelper", JSON.stringify(checked));
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    if (keycloak && keycloak.authenticated) {
      keycloak.logout({ redirectUri: window.location.origin });
    } else {
      window.location.reload();
    }
  };

  return (
    <Plan2PlateDrawer
      open={props.open}
      onClose={props.onClose}
      header={<Plan2PlateDrawerHeader heading="Einstellungen" />}
      body={
        <div className="flex flex-col gap-6 w-full pb-6">
          {/* Dark Mode */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-ink">Darstellung</span>
            <ColorModeSwitcher />
          </div>

          {/* Unit Helper Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-ink">
                Einheiten-Hilfe
              </span>
              <span className="text-xs text-ink-muted mt-0.5">
                Zeigt Einheiten über der Bildschirmtastatur an
              </span>
            </div>
            <Switch
              checked={showUnitHelper}
              onCheckedChange={(checked) => handleToggleUnitHelper(checked)}
            />
          </div>

          {/* Hinweise anzeigen */}
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-ink">
                Hinweise anzeigen
              </span>
              <span className="text-xs text-ink-muted mt-0.5">
                Blendet Hinweistexte in den Dialogen aus
              </span>
            </div>
            <Switch
              checked={props.showHints}
              onCheckedChange={(checked) => props.onShowHintsChange(checked)}
            />
          </div>

          {/* Tracking-Modus */}
          <div>
            <p className="text-sm font-bold text-ink mb-3">Tracking-Modus</p>
            <div className="flex flex-col gap-2">
              {MODES.map((mode) => {
                const active = props.trackingMode === mode;
                return (
                  <button
                    key={mode}
                    onClick={() => props.onTrackingModeChange(mode)}
                    className={`w-full text-left p-3 rounded-card border transition-colors ${
                      active
                        ? "border-teal bg-teal/5"
                        : "border-line bg-surface-card hover:bg-surface-raised"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-sm text-ink">
                        {TRACKING_MODE_LABELS[mode]}
                      </span>
                      {active && <Plan2PlateIcon icon="check" size="xs" />}
                    </div>
                    <p className="text-xs text-ink-muted mt-0.5">
                      {TRACKING_MODE_DESCRIPTIONS[mode]}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Gruppe */}
          <GroupSection />

          {/* Abmelden */}
          <div className="border-t border-line pt-6">
            <Button
              variant="destructive"
              fullWidth
              dataCy="settings-logout"
              onClick={handleLogout}
            >
              Abmelden
            </Button>
          </div>
        </div>
      }
    />
  );
};
